/**
 * ttFormat.js — formatting helpers shared by the Titan Treasure panels.
 * Every helper returns "—" for missing values so tables never show "NaN".
 */

const DASH = "—";
const isNum = (n) => n !== null && n !== undefined && !Number.isNaN(Number(n));

const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
const int = new Intl.NumberFormat("en-US");

export const money = (n) => (isNum(n) ? usd0.format(Number(n)) : DASH);
export const moneyExact = (n) => (isNum(n) ? usd2.format(Number(n)) : DASH);
export const count = (n) => (isNum(n) ? int.format(Number(n)) : DASH);

/** 0.1234 → "12.3%". */
export const pct = (fraction, digits = 1) =>
  isNum(fraction) ? `${(Number(fraction) * 100).toFixed(digits)}%` : DASH;

// The API sends naive UTC timestamps ("2026-11-03T10:00:00") and bare dates
// ("2026-10-26", e.g. dispute weeks); mark both UTC.
const toDate = (ts) => {
  if (!ts) return null;
  const s = String(ts);
  const d = new Date(
    /^\d{4}-\d\d-\d\d$/.test(s) ? `${s}T00:00:00Z` : /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

export const dateTime = (ts) => {
  const d = toDate(ts);
  return d
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : DASH;
};

export const dateOnly = (ts) => {
  const d = toDate(ts);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : DASH;
};

/** Calendar date in UTC, for dates that are days rather than moments (dispute deadlines). */
export const utcDate = (ts, withYear = true) => {
  const d = toDate(ts);
  return d
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear && { year: "numeric" }), timeZone: "UTC" })
    : DASH;
};

/** "2026-08" → "Aug 2026". */
export const monthLabel = (ym) => utcDate(`${ym}-01T00:00:00Z`).replace(/ \d+,/, "");

/** Axis label for a chart bucket. */
export const bucketLabel = (ts, bucket) => {
  const d = toDate(ts);
  if (!d) return "";
  return bucket === "hour"
    ? d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/** BIN + last 4 → "433993…1111". */
export const card = (bin, last4, cardKey) => {
  if (bin && last4) return `${bin}…${last4}`;
  if (cardKey && cardKey.length >= 10) return `${cardKey.slice(0, 6)}…${cardKey.slice(-4)}`;
  return cardKey || DASH;
};

export const shortId = (id, n = 10) => (!id ? DASH : id.length > n + 2 ? `${id.slice(0, n)}…` : id);

const PARTY_LABELS = { BLACKLISTED: "Blacklisted", WHITELISTED: "Whitelisted", FORCED_3DS: "Forced 3DS", NONE: "None" };
/** "NEEDS_RESPONSE" → "Needs response". */
export const humanize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, " ") : DASH);

/** "BLACKLISTED, FORCED_3DS" → "Blacklisted, Forced 3DS"; "WOULD_BLACKLIST" → "Would blacklist". */
export const partyLabel = (status) =>
  (status || "NONE")
    .split(",")
    .map((s) => PARTY_LABELS[s.trim()] || humanize(s.trim()))
    .join(", ");

export const OUTCOME_LABELS = {
  completed: "Completed",
  declined: "Declined",
  fraud_decline: "Fraud decline",
  blocked: "Blocked",
  abandoned: "Abandoned",
};

export const THREE_DS_LABELS = {
  frictionless: "Passed, no challenge",
  challenge_passed: "Passed after challenge",
  failed: "Failed",
  not_attempted: "Not attempted",
};

// Plain-words names for v3 rules, for screens Titan Treasure staff read.
// Unknown rules fall through unchanged.
const RULE_LABELS = {
  "Blacklisted User": "Restricted account",
  "Compromised BIN+4": "Compromised card",
  "Cumulative Abuse Daily Friction": "Unusual spending pattern",
  "Device reuse": "Shared device",
  "FORCE-3DS User": "Verification required",
  "Geo mismatch": "Location mismatch",
  "High Confidence Card Testing": "Card testing detected",
  "High Value Transaction Alert": "High value transaction",
  "High-risk BIN": "High risk card",
  "Low Value Slow Burn Activity": "Repeated small transactions",
  "Medium Value Transaction Alert": "Flagged transaction amount",
  "Test BIN": "Test card",
  "Test BIN (prod only)": "Test card",
  "VPN usage": "VPN detected",
  "Visitor reuse": "Shared device",
  "Visitor reuse High Risk": "Shared device",
  "Tampered environment": "Tampered device",
  "Whitelisted User": "Approved user",
};

export const ruleLabel = (rule) => {
  if (RULE_LABELS[rule]) return RULE_LABELS[rule];
  if (/^Velocity /.test(rule)) return "Transaction velocity exceeded";
  if (/^Visitor BIN Fanout/.test(rule)) return "Multiple cards on device";
  return rule;
};

/** Distinct plain-words reasons for a list of rules. */
export const friendlyReasons = (rules = [], enforced) => {
  const out = [];
  if (enforced === "blacklist") out.push("Restricted account");
  rules.forEach((r) => {
    const label = ruleLabel(r);
    if (!out.includes(label)) out.push(label);
  });
  return out.length ? out.join(", ") : DASH;
};
